// @ts-nocheck
import type { ValidationIssue } from '../../diagnostics';
import type { DataRef } from '../../types/canonical';
import type { ArbitraryFieldEntry } from '../bridge';
import { DEFAULT_COERCION_POLICY, readPropertyValue, writePropertyValue } from '../bridge';
import { resolveComputedRef } from './computedResolver';
import { resolveItemMetricFromCache } from '../metrics/ItemMetricsService';
import { resolveThemeColorFromContext } from './resolveThemeColor';
import type { ItemDefinition } from '../../../../../shims/appStub';
import { hasItemField, readItemFieldValue } from './itemFieldPolicy';
import type { BindingContext, BindingResolutionResult, BindingWriteResult } from './types';
import { itemHasClassId } from '../host/resolveItemClasses';
import { normalizeTemplateElementIdForLookup } from '../../../utils/TemplateIdUtils';
import { TemplateRuntimeRegistry } from '../../../../../shims/TemplateRuntimeRegistry';
import type { ItemVariableType } from '../variables/itemVariablePolicy';
import {
  applyItemVariableWrite,
  inferItemVariableWriteType,
  readItemVariableRef,
} from '../variables/itemVariableBridge';
import {
  getOrCreateLocalStateSession,
  writeLocalStateValue,
} from '../localState/LocalStateSessionStore';
import {
  lookupRelatedItem,
  readItemRefIdFromItemVariable,
} from './relatedItemBinding';

export interface WriteRefOptions {
  itemVariableType?: ItemVariableType;
}

/** Categories on item record, or inferred from category property entries when array is empty. */
function resolveEffectiveItemCategories(item: ItemDefinition): string[] {
  const direct = readItemFieldValue(item, 'categories');
  if (Array.isArray(direct) && direct.length > 0) {
    return direct.filter((id): id is string => typeof id === 'string' && id.length > 0);
  }
  const fromProperties = [...new Set(
    (item.arbitraryFieldsList ?? [])
      .map((entry) => entry.categoryId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  )];
  return fromProperties;
}

function membershipDiagnostic(message: string): ValidationIssue {
  return {
    severity: 'warning',
    code: 'semantic.missing_property',
    message,
  };
}

function resolveTemplateForMembership(
  context: BindingContext,
): BindingContext['templateDocument'] | undefined {
  if (context.templateDocument) {
    return context.templateDocument;
  }
  const templateId = context.item.templateId?.trim();
  if (!templateId) {
    return undefined;
  }
  return TemplateRuntimeRegistry.getTemplate(templateId);
}

/**
 * Resolves a DataRef to a typed value using the provided context.
 * Never throws. Returns a BindingResolutionResult with found === false for
 * unresolvable or out-of-scope refs.
 */
export function resolveRef(ref: DataRef, context: BindingContext): BindingResolutionResult {
  switch (ref.kind) {
    case 'indexedItemField': {
      if (!Number.isInteger(ref.index) || ref.index < 0) {
        const issue: ValidationIssue = {
          severity: 'warning',
          code: 'semantic.invalid_data_ref',
          message: 'Invalid image index: ' + String(ref.index) + '. Expected non-negative integer.',
        };
        return { found: false, source: 'itemField', diagnostics: [issue] };
      }

      const uris = context.item.imageUris;
      if (!Array.isArray(uris)) {
        const issue: ValidationIssue = {
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message: 'Item field imageUris is not available for indexed binding.',
        };
        return { found: false, source: 'itemField', diagnostics: [issue] };
      }

      if (ref.index >= uris.length) {
        const issue: ValidationIssue = {
          severity: 'warning',
          code: 'semantic.invalid_data_ref',
          message: 'Image index out of bounds: ' + ref.index + ' (available length: ' + uris.length + ').',
        };
        return { found: false, source: 'itemField', diagnostics: [issue] };
      }

      const value = uris[ref.index];
      if (value === undefined) {
        return { found: false, source: 'itemField' };
      }

      return { found: true, value, source: 'itemField' };
    }

    case 'itemField': {
      if (ref.field === 'categories') {
        const effective = resolveEffectiveItemCategories(context.item);
        return { found: true, value: effective, source: 'itemField' };
      }
      if (ref.field === 'dimensionsHeight' || ref.field === 'dimensionsWidth') {
        const primary = readItemFieldValue(context.item, ref.field);
        if (typeof primary === 'number' && Number.isFinite(primary)) {
          return { found: true, value: primary, source: 'itemField' };
        }
        const fallbackField = ref.field === 'dimensionsHeight' ? 'canvasH' : 'canvasW';
        const fallback = readItemFieldValue(context.item, fallbackField);
        if (typeof fallback === 'number' && Number.isFinite(fallback)) {
          return { found: true, value: fallback, source: 'itemField' };
        }
        if (primary === null) {
          return { found: true, value: null, source: 'itemField' };
        }
      }
      if (!hasItemField(context.item, ref.field)) {
        const issue: ValidationIssue = {
          severity: 'warning',
          code: 'semantic.missing_property',
          message: 'Unknown item field: ' + ref.field,
        };
        return { found: false, source: 'itemField', diagnostics: [issue] };
      }
      const value = readItemFieldValue(context.item, ref.field);
      if (value === undefined) {
        return { found: false, value: undefined, source: 'itemField' };
      }
      return { found: true, value, source: 'itemField' };
    }

    case 'categoryProperty': {
      const category = context.compiledTemplate.categoriesById[ref.categoryId];
      if (!category) {
        const issue: ValidationIssue = {
          severity: 'error',
          code: 'semantic.missing_property',
          message: 'Category or property not found: ' + ref.categoryId + '.' + ref.propertyId,
        };
        return { found: false, source: 'categoryProperty', diagnostics: [issue] };
      }
      const property = category.properties.find(p => p.id === ref.propertyId);
      if (!property) {
        const issue: ValidationIssue = {
          severity: 'error',
          code: 'semantic.missing_property',
          message: 'Category or property not found: ' + ref.categoryId + '.' + ref.propertyId,
        };
        return { found: false, source: 'categoryProperty', diagnostics: [issue] };
      }

      let sourceItem: ItemDefinition = context.item;
      if (ref.itemFrom != null) {
        if (ref.itemFrom.kind !== 'itemVariable' || !ref.itemFrom.key?.trim()) {
          const issue: ValidationIssue = {
            severity: 'warning',
            code: 'semantic.invalid_data_ref',
            message: 'categoryProperty.itemFrom must be { kind: "itemVariable", key } with a non-empty key.',
          };
          return { found: false, source: 'categoryProperty', diagnostics: [issue] };
        }
        if (
          context.capabilities != null
          && !context.capabilities.has('read.relatives')
        ) {
          const issue: ValidationIssue = {
            severity: 'warning',
            code: 'policy.capability_not_allowed',
            message:
              'Cross-item categoryProperty.itemFrom requires capability read.relatives '
              + `(key: ${ref.itemFrom.key}).`,
          };
          return { found: false, source: 'categoryProperty', diagnostics: [issue] };
        }
        const relatedId = readItemRefIdFromItemVariable(context.item, ref.itemFrom.key);
        if (relatedId == null) {
          const issue: ValidationIssue = {
            severity: 'info',
            code: 'semantic.invalid_data_ref',
            message:
              `categoryProperty.itemFrom itemVariable '${ref.itemFrom.key}' `
              + 'is missing or not a valid itemRef.',
          };
          return { found: false, source: 'categoryProperty', diagnostics: [issue] };
        }
        if (relatedId !== Number(context.item.id)) {
          const related = lookupRelatedItem(context, relatedId);
          if (!related) {
            const issue: ValidationIssue = {
              severity: 'warning',
              code: 'semantic.missing_property',
              message:
                `Related item ${relatedId} for itemFrom '${ref.itemFrom.key}' `
                + 'was not available in the binding context.',
            };
            return { found: false, source: 'categoryProperty', diagnostics: [issue] };
          }
          sourceItem = related;
        }
      }

      // PropertyDefinition structural shape matches what readPropertyValue expects.
      const definition = property as import('../../types/canonical').PropertyDefinition;
      const fields: ArbitraryFieldEntry[] = (sourceItem.arbitraryFieldsList ?? []).map(f => ({
        categoryId: f.categoryId,
        propertyId: f.propertyId,
        value: f.value,
      }));

      const readResult = readPropertyValue(
        ref.categoryId,
        ref.propertyId,
        fields,
        definition,
        DEFAULT_COERCION_POLICY,
      );

      return {
        found: readResult.found || readResult.usedDefault,
        value: readResult.typedValue,
        source: 'categoryProperty',
        diagnostics: readResult.issues.length > 0 ? readResult.issues : undefined,
      };
    }

    case 'computed': {
      return resolveComputedRef(ref.computedId, context);
    }

    case 'localState': {
      if (context.localState != null && Object.prototype.hasOwnProperty.call(context.localState, ref.key)) {
        return { found: true, value: context.localState[ref.key], source: 'localState' };
      }
      const issue: ValidationIssue = {
        severity: 'info',
        code: 'schema.invalid_field_type',
        message: 'Local state key not found in script scope (key: ' + ref.key + ')',
      };
      return { found: false, source: 'localState', diagnostics: [issue] };
    }

    case 'runtimeContext': {
      const hostValue = context.hostContext?.[ref.key];
      if (hostValue !== undefined) {
        return { found: true, value: hostValue, source: 'runtimeContext' };
      }
      if (ref.key === 'currentDateTime') {
        return { found: true, value: new Date().toISOString(), source: 'runtimeContext' };
      }
      const issue: ValidationIssue = {
        severity: 'info',
        code: 'schema.invalid_field_type',
        message: 'Runtime context key not supported (key: ' + ref.key + ')',
      };
      return { found: false, source: 'runtimeContext', diagnostics: [issue] };
    }

    case 'itemMetric': {
      const cached = resolveItemMetricFromCache(ref.metric, context);
      if (cached !== undefined) {
        return { found: true, value: cached, source: 'itemMetric' };
      }
      const issue: ValidationIssue = {
        severity: 'warning',
        code: 'semantic.missing_property',
        message: 'Item metric not available in current pass (metric: ' + ref.metric + ')',
      };
      return { found: false, source: 'itemMetric', diagnostics: [issue] };
    }

    case 'expression': {
      const issue: ValidationIssue = {
        severity: 'warning',
        code: 'semantic.invalid_data_ref',
        message: 'Expression library refs must be evaluated by the expression engine, not BindingResolver.',
      };
      return { found: false, source: 'constant', diagnostics: [issue] };
    }

    case 'themeColor': {
      const color = resolveThemeColorFromContext(ref, context);
      if (color !== undefined) {
        return { found: true, value: color, source: 'themeColor' };
      }
      const issue: ValidationIssue = {
        severity: 'warning',
        code: 'semantic.missing_property',
        message: 'Theme color not available (key: ' + ref.key + ')',
      };
      return { found: false, source: 'themeColor', diagnostics: [issue] };
    }

    case 'itemVariable': {
      return readItemVariableRef(ref, context.item);
    }

    case 'itemHasClass': {
      const classId = typeof ref.classId === 'string' ? ref.classId.trim() : '';
      if (!classId) {
        return {
          found: true,
          value: false,
          source: 'itemHasClass',
          diagnostics: [membershipDiagnostic('itemHasClass requires a non-empty classId.')],
        };
      }
      const template = resolveTemplateForMembership(context);
      if (!template) {
        return {
          found: true,
          value: false,
          source: 'itemHasClass',
          diagnostics: [membershipDiagnostic(
            'itemHasClass template lookup is unavailable (classId: ' + classId + ')',
          )],
        };
      }
      return {
        found: true,
        value: itemHasClassId(context.item, classId, template),
        source: 'itemHasClass',
      };
    }

    case 'itemHasCategory': {
      const categoryId = typeof ref.categoryId === 'string' ? ref.categoryId.trim() : '';
      if (!categoryId) {
        return {
          found: true,
          value: false,
          source: 'itemHasCategory',
          diagnostics: [membershipDiagnostic('itemHasCategory requires a non-empty categoryId.')],
        };
      }
      const categories = resolveEffectiveItemCategories(context.item);
      return {
        found: true,
        value: categories.includes(categoryId),
        source: 'itemHasCategory',
      };
    }

    case 'itemHasElement': {
      const wanted = normalizeTemplateElementIdForLookup(ref.elementId ?? '');
      if (!wanted) {
        return {
          found: true,
          value: false,
          source: 'itemHasElement',
          diagnostics: [membershipDiagnostic('itemHasElement requires a non-empty elementId.')],
        };
      }
      const current = normalizeTemplateElementIdForLookup(context.item.templateElementId);
      return {
        found: true,
        value: wanted.length > 0 && wanted === current,
        source: 'itemHasElement',
      };
    }
  }
}

/**
 * Writes a value through a DataRef, returning the updated state.
 * Never mutates context. Returns BindingWriteResult.
 * For itemField writes: not supported in M2 (returns diagnostic).
 * For categoryProperty writes: returns updatedFields with the new arbitraryFieldsList.
 */
export function writeRef(
  ref: DataRef,
  value: unknown,
  context: BindingContext,
  options?: WriteRefOptions,
): BindingWriteResult {
  switch (ref.kind) {
    case 'indexedItemField':
    case 'itemField': {
      const issue: ValidationIssue = {
        severity: 'warning',
        code: 'policy.capability_not_allowed',
        message: 'Direct itemField writes are not supported in M2 — use a data mutation service',
      };
      return { success: false, diagnostics: [issue] };
    }

    case 'categoryProperty': {
      if (ref.itemFrom != null) {
        const issue: ValidationIssue = {
          severity: 'warning',
          code: 'policy.capability_not_allowed',
          message:
            'Writes through categoryProperty.itemFrom are not supported — '
            + 'mutate the related item via host APIs (itemId).',
        };
        return { success: false, diagnostics: [issue] };
      }
      const category = context.compiledTemplate.categoriesById[ref.categoryId];
      if (!category) {
        const issue: ValidationIssue = {
          severity: 'error',
          code: 'semantic.missing_property',
          message: 'Category or property not found: ' + ref.categoryId + '.' + ref.propertyId,
        };
        return { success: false, diagnostics: [issue] };
      }
      const property = category.properties.find(p => p.id === ref.propertyId);
      if (!property) {
        const issue: ValidationIssue = {
          severity: 'error',
          code: 'semantic.missing_property',
          message: 'Category or property not found: ' + ref.categoryId + '.' + ref.propertyId,
        };
        return { success: false, diagnostics: [issue] };
      }

      const definition = property as import('../../types/canonical').PropertyDefinition;
      const fields: ArbitraryFieldEntry[] = (context.item.arbitraryFieldsList ?? []).map(f => ({
        categoryId: f.categoryId,
        propertyId: f.propertyId,
        value: f.value,
      }));

      const writeResult = writePropertyValue(
        ref.categoryId,
        ref.propertyId,
        value,
        fields,
        definition,
        DEFAULT_COERCION_POLICY,
      );

      return {
        success: writeResult.success,
        updatedFields: writeResult.updatedFields,
        diagnostics: writeResult.issues.length > 0 ? writeResult.issues : undefined,
      };
    }

    case 'itemVariable': {
      const variableType = inferItemVariableWriteType(
        context.item,
        ref.key,
        options?.itemVariableType,
      );
      const writeResult = applyItemVariableWrite(
        context.item,
        ref.key,
        variableType,
        value,
      );
      return {
        success: writeResult.success,
        updatedItemVariablesJson: writeResult.updatedItemVariablesJson,
        diagnostics: writeResult.diagnostics,
      };
    }

    case 'localState': {
      const itemId = typeof context.item.id === 'number' ? context.item.id : Number(context.item.id);
      const bag = context.localState
        ?? (Number.isFinite(itemId)
          ? getOrCreateLocalStateSession(itemId)
          : {});
      if (context.localState == null) {
        context.localState = bag;
      }
      const writeResult = writeLocalStateValue(bag, ref.key, value);
      if (!writeResult.ok) {
        const issue: ValidationIssue = {
          severity: 'error',
          code: 'semantic.invalid_data_ref',
          message: writeResult.message,
        };
        return { success: false, diagnostics: [issue] };
      }
      return {
        success: true,
        updatedLocalState: bag,
      };
    }

    case 'computed':
    case 'expression':
    case 'runtimeContext':
    case 'itemMetric':
    case 'themeColor':
    case 'itemHasClass':
    case 'itemHasCategory':
    case 'itemHasElement': {
      const issue: ValidationIssue = {
        severity: 'warning',
        code: 'policy.capability_not_allowed',
        message: 'Write not supported for ref kind: ' + ref.kind + ' in M2',
      };
      return { success: false, diagnostics: [issue] };
    }
  }
}
